import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  IconButton as MuiIconButton,
  MobileStepper,
  Checkbox,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Close as CloseIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Edit as EditIcon,
  AutoFixHigh
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import LazyImage from '../../components/common/LazyImage';
import { FormControlLabel, Switch } from '@mui/material';
import { API_ENDPOINTS } from '../../config/api';

// Import bölümüne yeni ikonları ekleyelim
import {
  Share as ShareIcon,
  People as PeopleIcon
} from '@mui/icons-material';

const EventDetail = () => {
  // State tanımlamaları
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSelectedConfirmOpen, setDeleteSelectedConfirmOpen] = useState(false);
  const [deleteImageConfirmOpen, setDeleteImageConfirmOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [uploadedByUser, setUploadedByUser] = useState(null);
  const [showQRLogo, setShowQRLogo] = useState(false);
  const [planFeatures, setPlanFeatures] = useState([]);
  const [showInvitationDesign, setShowInvitationDesign] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  const { codeHash } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = event?.user_id == user?.id;
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [shareEmail, setShareEmail] = useState('');
  // Paylaşım izinlerini kontrol etmek için state ekleyelim
  const [sharePermissions, setSharePermissions] = useState({
    canRead: false,
    canDelete: false
  });
  const [hasDeletePermission, setHasDeletePermission] = useState(isOwner || (sharePermissions.canDelete ?? false));

  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventType: '',
    event_type_id: '',
    image_data: '',
    event_type_name: ''
  });

  // Plan limiti API'den çekilecek fonksiyon
  const fetchFileLimit = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.plans.planFileLimit(codeHash), {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.features && Array.isArray(data.features)) {
          setPlanFeatures(data.features);
          // Özelliklere göre butonları ayarla
          const invitationFeature = data.features.find(f => f.feature_key === 'davetiye_tasarimi');
          const participantsFeature = data.features.find(f => f.feature_key === 'katilimci_listesi');
          setShowInvitationDesign(invitationFeature && (invitationFeature.feature_value === '1' || invitationFeature.feature_value === 'true'));
          setShowParticipants(participantsFeature && (participantsFeature.feature_value === '1' || participantsFeature.feature_value === 'true'));
        } else {
          setShowInvitationDesign(false);
          setShowParticipants(false);
        }
      }
    } catch (e) {
      setShowInvitationDesign(false);
      setShowParticipants(false);
    }
  }, [codeHash]);

  useEffect(() => {
    fetchEventDetails();
    fetchUploads();
    fetchFileLimit();
  }, [codeHash, fetchFileLimit]);

  useEffect(() => {
    if (uploads.length > 0) {
      loadImagesWithDelay();
    }
  }, [uploads]);

  useEffect(() => {
    if (user) {
      setUploadedByUser(user.email);
    }
  }, [user]);

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.getByCode(codeHash), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Etkinlik bulunamadı');
      setEvent(data);
      setEditFormData({
        title: data.name,
        description: data.description || '',
        eventType: data.event_type_id,
        event_type_id: data.event_type_id,
        image_data: data.image_data,
        eventDate: data.event_date ? data.event_date.split('T')[0] : '',
        event_type_name: data.event_type_name
      });
      // Paylaşım izinlerini ayarla
      if (data) {
        setSharePermissions({
          canRead: Boolean(Number(data.can_read)),
          canDelete: Boolean(Number(data.can_delete))
        });
        if (data?.user_id != user?.id) {
          setHasDeletePermission(Boolean(Number(data.can_delete)));
        } else {
          setHasDeletePermission(true);
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.getUploads(codeHash), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      if (response.ok) {
        const filteredData = uploadedByUser
          ? data.filter(upload => upload.uploaded_by_email === uploadedByUser)
          : data;
        setUploads(filteredData);
      }
    } catch {
      toast.error('Dosyalar yüklenirken bir hata oluştu');
    }
  };

  const loadImagesWithDelay = () => {
    uploads.forEach((upload, index) => {
      if (upload.file_type?.startsWith('image/')) {
        setTimeout(() => {
          const img = new Image();
          img.src = `https://drive.google.com/thumbnail?id=${upload.drive_file_id}&sz=w100`;
        }, index * 300);
      }
    });
  };

  const canDeleteFile = (upload) => {
    return isOwner || (user && upload.uploaded_by_email === user.email);
  };

  const handleSelectAll = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedFiles(uploads.map(upload => upload.id));
    } else {
      setSelectedFiles([]);
    }
  };

  const handleDeleteEvent = () => {
    setDeleteConfirmOpen(true);
  };

  const handleSelectFile = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  const handleConfirmDeleteEvent = async () => {
    setDeleteConfirmOpen(false);
    try {
      const response = await fetch(API_ENDPOINTS.events.delete(codeHash), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          silindi: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Silme işlemi başarısız oldu');
      }

      toast.success('Etkinlik başarıyla silindi');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteSelected = () => {
    setDeleteSelectedConfirmOpen(true);
  };

  const handleConfirmDeleteSelected = async () => {
    setDeleteSelectedConfirmOpen(false);
    try {
      const deletePromises = selectedFiles.map(async (fileId) => {
        await new Promise(resolve => setTimeout(resolve, 500)); // Her dosya için 500ms gecikme

        const response = await fetch(API_ENDPOINTS.events.deleteUpload(codeHash, fileId), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Bilinmeyen bir hata oluştu');
        }

        return response;
      });

      await Promise.all(deletePromises);

      setUploads(prev => prev.filter(upload => !selectedFiles.includes(upload.id)));
      setSelectedFiles([]);
      setSelectAll(false);
      toast.success('Seçili dosyalar başarıyla silindi');
    } catch (error) {
      toast.error(error.message || 'Dosyalar silinirken bir hata oluştu');
    }
  };

  const downloadQRCode = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-code-${codeHash}.png`;
      link.href = url;
      link.click();
    }
  };

  const handlePreviewOpen = (index) => {
    setActiveStep(index);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === uploads.length - 1 ? 0 : prevActiveStep + 1
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === 0 ? uploads.length - 1 : prevActiveStep - 1
    );
  };

  const handleEditDialogOpen = () => {
    setEditFormData({
      title: event.name,
      description: event.description || '',
      event_type_id: event.event_type_id,
      eventDate: event.event_date ? event.event_date.split('T')[0] : '',
      image_data: event.image_data ? event.image_data : '',
      event_type_name: event.event_type_name || '',
      eventType: event.event_type_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const generateEventDescription = async () => {
    try {
      setIsGeneratingContent(true);

      const selectedType = eventTypes.find(type => type.id === editFormData.eventType);
      const prompt = `${selectedType?.name || 'Etkinlik'} davetimiz için katılımcıların masadaki qr'ı okutup davette fotoğraf videoları yüklemesi için bir açıklama yaz. Sadece bana açıklamayı ver. ${'Etkinlik adımız: ' + editFormData?.title + '. Etkinlik adı yapacağın açıklamada en başında yazmasın sonunda Sevgiyle, Etkinlik Adı şeklinde yazabilir.' || 'Etkinlik adı bulunmuyor.'}`;

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const response = await fetch(API_ENDPOINTS.ai.generate(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          question: prompt,
          model: 'meta-ai'
        })
      });

      if (!response.ok) throw new Error('API yanıt vermedi');

      const data = await response.json();
      console.log(data.response);
      console.log("Gelen yanıt:", data.response);

      let description = data.response;
      const unwantedText = "I am still improving my command of other languages, and I may make errors while attempting them.";
      setEditFormData(prev => ({
        ...prev,
        description: description.replaceAll(unwantedText, "")
      }));

      toast.success('Açıklama oluşturuldu');
    } catch (error) {
      console.error(error);
      toast.error('İçerik oluşturulurken bir hata oluştu: ' + error.message);
    } finally {
      setIsGeneratingContent(false);
    }
  };


  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const [eventTypes, setEventTypes] = useState([]);

  // Event türlerini getir
  const fetchEventTypes = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.eventTypes.getAll(), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      if (response.ok) {
        setEventTypes(data);
      }
    } catch (error) {
      toast.error('Etkinlik türleri alınırken bir hata oluştu');
    }
  };

  useEffect(() => {
    if (event) {
      setEditFormData({
        title: event.name,
        description: event.description || '',
        eventDate: event.event_date ? event.event_date.split('T')[0] : '',
        eventType: event.event_type_id || '',
        event_type_id: event.event_type_id || '',
        image_data: event.image_data || '',
      });
      fetchEventTypes();
    }
  }, [event]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Görüntüyü bir Image nesnesine yükle
        const img = new Image();
        img.onload = () => {
          // Canvas oluştur
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Maksimum boyutlar (oranı koruyarak)
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          // Canvas boyutlarını ayarla
          canvas.width = width;
          canvas.height = height;

          // Görüntüyü canvas'a çiz
          ctx.drawImage(img, 0, 0, width, height);

          // Sıkıştırılmış base64 görüntüsünü al (0.8 kalite)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);

          // Base64 boyutunu kontrol et (yaklaşık olarak)
          const base64Size = Math.round((compressedBase64.length - 'data:image/jpeg;base64,'.length) * 0.75 / 1024 / 1024);

          if (base64Size > 2) {
            toast.error('Görsel boyutu 2MB\'dan büyük. Lütfen daha küçük bir görsel seçin.');
            return;
          }

          setEditFormData(prev => ({
            ...prev,
            image_data: compressedBase64
          }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateEvent = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.update(codeHash), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          name: editFormData.title,
          description: editFormData.description,
          eventDate: editFormData.eventDate,
          eventType: editFormData.eventType,
          image_data: editFormData.image_data
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Güncelleme sırasında bir hata oluştu');
      }

      setEvent(prev => ({
        ...prev,
        name: editFormData.title,
        description: editFormData.description,
        event_date: editFormData.eventDate,
        event_type_id: editFormData.eventType,
        event_type_name: editFormData.event_type_name,
        image_data: editFormData.image_data
      }));

      toast.success('Etkinlik başarıyla güncellendi');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.message);
    }
  };
  // Paylaşımları getiren fonksiyon
  const fetchShares = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.getShares(event.id), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      if (response.ok) {
        setShares(data);
      }
    } catch (error) {
      toast.error('Paylaşım listesi alınırken bir hata oluştu');
    }
  };

  // Paylaşım dialog'unu açma fonksiyonu
  const handleShareDialogOpen = () => {
    setShareDialogOpen(true);
    fetchShares();
  };

  // Paylaşım dialog'unu kapatma fonksiyonu
  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setShareEmail('');
    setSharePermissions({ canRead: true, canDelete: false });
  };

  // Paylaşım ekleme fonksiyonu
  const handleShare = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.events.share(event.id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          email: shareEmail,
          ...sharePermissions
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast.success('Etkinlik başarıyla paylaşıldı');
      fetchShares();
      setShareEmail('');
    } catch (error) {
      toast.error(error.message || 'Paylaşım sırasında bir hata oluştu');
    }
  };

  // Paylaşım silme fonksiyonu
  const handleRemoveShare = async (shareId) => {
    try {
      const response = await fetch(API_ENDPOINTS.events.removeShare(event.id, shareId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      toast.success('Paylaşım başarıyla kaldırıldı');
      fetchShares();
    } catch (error) {
      toast.error(error.message || 'Paylaşım kaldırılırken bir hata oluştu');
    }
  };

  // Paylaşım izinlerini güncelleme fonksiyonu
  const handleUpdateSharePermissions = async (shareId, permissions) => {
    try {
      const response = await fetch(API_ENDPOINTS.events.updateShare(event.id, shareId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(permissions)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      toast.success('Paylaşım izinleri güncellendi');
      fetchShares();
    } catch (error) {
      toast.error(error.message || 'İzinler güncellenirken bir hata oluştu');
    }
  };

  const renderPreviewContent = (file) => {
    if (!file) return null;

    const mime = file.file_type;

    if (mime?.startsWith('image/')) {
      return (
        <img
          src={`https://drive.google.com/uc?id=${file.drive_file_id}`}
          alt={file.file_name}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://drive.google.com/thumbnail?id=${file.drive_file_id}&sz=w800`;
          }}
        />
      );
    }

    if (mime?.startsWith('video/')) {
      return (
        <video
          controls
          autoPlay
          style={{ maxWidth: '100%', maxHeight: '70vh' }}
          playsInline
        >
          <source
            src={`https://drive.google.com/uc?export=download&id=${file.drive_file_id}`}
            type={mime}
          />
          Tarayıcınız video etiketini desteklemiyor.
        </video>
      );
    }

    if (mime === 'application/pdf') {
      return (
        <iframe
          src={`https://drive.google.com/file/d/${file.drive_file_id}/preview`}
          width="100%"
          height="70vh"
          style={{ border: 'none' }}
          title="PDF Önizleme"
        />
      );
    }

    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh'
      }}>
        <InsertDriveFileIcon sx={{ fontSize: 100, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6">{file.file_name}</Typography>
        <Typography variant="body2" color="text.secondary">{file.file_type}</Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{ mt: 2 }}
          onClick={() => window.open(`https://drive.google.com/uc?id=${file.drive_file_id}&export=download`, '_blank')}
        >
          İndir
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6" color="error">Etkinlik bulunamadı</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {event.name}
                </Typography>
                {event.event_type_id && (
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {
                      eventTypes.find(type => type.id === event.event_type_id)?.name
                    }
                  </Typography>
                )}
              </Box>
              <Typography variant="body1" paragraph>
                {event.description || 'Açıklama bulunmuyor'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Etkinlik Tarihi: {event.event_date ? new Date(event.event_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Paper elevation={3} sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '##a6ad8' }}>
                    {event.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Etkinliğe dosya yüklemek için QR kodu okutun veya aşağıdaki butona tıklayın
                  </Typography>
                  <Box sx={{
                    p: 3,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '2px dashed #7a6ad8',
                    mb: 2
                  }}>
                    <QRCodeCanvas
                      value={`${window.location.origin}/upload/${codeHash}`}
                      size={200}
                      level="H"
                      imageSettings={showQRLogo ? {
                        src: '/logo192.png',
                        height: 40,
                        width: 40,
                        excavate: true
                      } : null}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showQRLogo}
                          onChange={(e) => setShowQRLogo(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="QR Kod Ortasında Logo Göster"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadQRCode}
                    sx={{ mb: 1 }}
                    fullWidth
                  >
                    QR Kodu İndir
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => window.open(`${window.location.origin}/upload/${codeHash}`, '_blank')}
                    fullWidth
                  >
                    Dosya Yükleme Sayfasına Git
                  </Button>
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  alignItems: 'flex-start',
                  mb: 3,
                  position: { xs: 'static', md: 'absolute' },
                  right: { md: 0 },
                  top: { md: 0 },
                  zIndex: 2,
                  pr: { md: 2 },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                  {showParticipants && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate(`/event/${codeHash}/participants`)}
                      startIcon={<PeopleIcon />}
                      sx={{ minWidth: 160 }}
                    >
                      Katılımcı Listesi
                    </Button>
                  )}
                  {showInvitationDesign && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => navigate(`/event/${codeHash}/invitation-design`)}
                      sx={{ minWidth: 160 }}
                    >
                      Davetiye Tasarla
                    </Button>
                  )}
                  {/* Diğer butonlar tek satırda ve sağa yaslı */}
                  <Box sx={{ display: 'flex', flexDirection: 'row', ml: 'auto', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={handleShareDialogOpen} size="small" color="primary">
                      <ShareIcon />
                    </IconButton>
                    <IconButton onClick={handleEditDialogOpen} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={handleDeleteEvent} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Stack>
              </Box>
              {event.image_data && (
                <Box sx={{ mt: 10 }}>
                  {isOwner && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          backgroundColor: 'primary.main',
                        },
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                          transition: 'all 0.3s ease-in-out'
                        }
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: '1.1rem',
                          letterSpacing: '0.5px',
                          pl: 2
                        }}
                      >
                        Karşılama Önizleme Sayfası
                      </Typography>
                      <IconButton
                        onClick={() => setDeleteImageConfirmOpen(true)}
                        size="small"
                        color="error"
                        sx={{
                          backgroundColor: 'error.lighter',
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            transform: 'rotate(90deg)',
                            '& .MuiSvgIcon-root': {
                              color: 'common.white',
                              transform: 'scale(1.2)'
                            }
                          },
                          transition: 'all 0.3s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            transition: 'all 0.3s ease-in-out'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <div style={{ position: 'relative', maxHeight: '600px', height: 'auto', borderRadius: '10px', overflow: 'hidden' }}>
                      {/* Üst karartma ve yazılar */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '8%',
                        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent)',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingBottom: '10px',
                        paddingTop: '20px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{event.event_date ? new Date(event.event_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</div>
                      </div>
                      <img
                        src={event.image_data}
                        alt="Etkinlik görseli önizleme"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />

                      {/* Alt karartma ve yazılar */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingBottom: '10px',
                        paddingTop: '20px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{event.name || ''}</div>
                        <div style={{ fontSize: '14px', opacity: 0.9, color: "white", padding: "10px" }}>{event.description || ''}</div>

                      </div>
                    </div>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Yüklenen Dosyalar
          </Typography>
          <Stack direction="row" spacing={2}>
            {hasDeletePermission && selectedFiles.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteSelected}
              >
                Seçili Dosyaları Sil ({selectedFiles.length})
              </Button>
            )}
          </Stack>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {hasDeletePermission && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedFiles.length > 0 && selectedFiles.length < uploads.length}
                    />
                  </TableCell>
                )}
                <TableCell>Önizleme</TableCell>
                <TableCell>Dosya Adı</TableCell>
                <TableCell>Boyut</TableCell>
                <TableCell>Tür</TableCell>
                <TableCell>Yükleyen</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((upload, index) => (
                <TableRow
                  key={upload.id}
                  sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  {hasDeletePermission && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedFiles.includes(upload.id)}
                        onChange={() => handleSelectFile(upload.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <LazyImage
                      driveFileId={upload.drive_file_id}
                      fileName={upload.file_name}
                      delay={index * 300}
                    />
                  </TableCell>
                  <TableCell>{upload.file_name}</TableCell>
                  <TableCell>{Math.round(upload.file_size / 1024)} KB</TableCell>
                  <TableCell>{upload.file_type}</TableCell>
                  <TableCell>{upload.uploaded_by_email}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Önizle">
                      <IconButton
                        onClick={() => handlePreviewOpen(index)}
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="İndir">
                      <IconButton
                        onClick={() => window.open(`https://drive.google.com/uc?id=${upload.drive_file_id}&export=download`, '_blank')}
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {uploads[activeStep]?.file_name}
            </Typography>
            <IconButton onClick={handlePreviewClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderPreviewContent(uploads[activeStep])}
          <MobileStepper
            steps={uploads.length}
            position="static"
            activeStep={activeStep}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                disabled={uploads.length <= 1}
              >
                İleri
                <KeyboardArrowRight />
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={uploads.length <= 1}
              >
                <KeyboardArrowLeft />
                Geri
              </Button>
            }
          />
        </DialogContent>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Etkinliği Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Etkinlik Adı"
              name="title"
              value={editFormData.title}
              onChange={handleEditFormChange}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Açıklama"
                name="description"
                multiline
                rows={4}
                value={editFormData.description}
                onChange={handleEditFormChange}
                sx={{ flex: 1 }}
              />
              <Tooltip title="AI ile oluştur" arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={generateEventDescription}
                    disabled={isGeneratingContent || !editFormData.eventType}
                    sx={{
                      minWidth: 'auto',
                      alignSelf: 'flex-start',
                      height: '100%',
                      padding: 0,
                      width: 48,
                      height: 56, // TextField'ın yüksekliğine yakın (varsayılan 56px)
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isGeneratingContent ? (
                      <CircularProgress size={24} />
                    ) : (
                      <AutoFixHigh fontSize="medium" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {!editFormData.eventType && (
              <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
                AI ile içerik oluşturmak için önce etkinlik türü seçmelisiniz.
              </Typography>
            )}
            <TextField
              fullWidth
              label="Etkinlik Tarihi"
              name="eventDate"
              type="date"
              value={editFormData.eventDate}
              onChange={handleEditFormChange}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              fullWidth
              label="Etkinlik Türü"
              name="eventType"
              value={editFormData.event_type_id}
              onChange={handleEditFormChange}
              sx={{ mb: 2 }}
            >
              {eventTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="event-image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="event-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Etkinlik Görseli Yükle
                </Button>
              </label>
              {editFormData.image_data && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={editFormData.image_data}
                    alt="Etkinlik görseli önizleme"
                    style={{ width: 'auto', maxHeight: '200px' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>İptal</Button>
          <Button onClick={handleUpdateEvent} variant="contained" color="primary">
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Etkinliği Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu etkinliği silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleConfirmDeleteEvent} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteSelectedConfirmOpen}
        onClose={() => setDeleteSelectedConfirmOpen(false)}
      >
        <DialogTitle>Seçili Dosyaları Sil</DialogTitle>
        <DialogContent>
          <Typography>{`Seçili ${selectedFiles.length} dosyayı silmek istediğinizden emin misiniz?`}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSelectedConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleIcon />
            <Typography>Etkinliği Paylaş</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="E-posta Adresi"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              margin="normal"
              type="email"
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={sharePermissions.canRead}
                  onChange={(e) => setSharePermissions(prev => ({ ...prev, canRead: e.target.checked }))}
                />
              }
              label="Görüntüleme İzni"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={sharePermissions.canDelete}
                  onChange={(e) => setSharePermissions(prev => ({ ...prev, canDelete: e.target.checked }))}
                />
              }
              label="Silme İzni"
            />
            <Button
              variant="contained"
              onClick={handleShare}
              fullWidth
              sx={{ mt: 2 }}
              disabled={!shareEmail}
            >
              Paylaş
            </Button>

            {shares.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>Paylaşılan Kişiler</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>E-posta</TableCell>
                        <TableCell align="center">Görüntüleme</TableCell>
                        <TableCell align="center">Silme</TableCell>
                        <TableCell align="right">İşlemler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shares.map((share) => (
                        <TableRow key={share.id}>
                          <TableCell>{share.shared_with_email}</TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={Boolean(share.can_read)}
                              onChange={(e) => handleUpdateSharePermissions(share.id, {
                                canRead: e.target.checked,
                                canDelete: share.can_delete
                              })}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={Boolean(share.can_delete)}
                              onChange={(e) => handleUpdateSharePermissions(share.id, {
                                canRead: share.can_read,
                                canDelete: e.target.checked
                              })}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveShare(share.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareDialogClose}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteImageConfirmOpen} onClose={() => setDeleteImageConfirmOpen(false)}>
        <DialogTitle>Görseli Kaldır</DialogTitle>
        <DialogContent>
          <Typography>Karşılama önizleme sayfası görselini kaldırmak istediğinizden emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteImageConfirmOpen(false)}>İptal</Button>
          <Button
            onClick={async () => {
              try {
                const response = await fetch(API_ENDPOINTS.events.update(codeHash), {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                  },
                  body: JSON.stringify({
                    ...event,
                    gorselSil: 1
                  })
                });

                if (!response.ok) throw new Error('Görsel kaldırılamadı');

                setEvent(prev => ({
                  ...prev,
                  image_data: null
                }));

                setDeleteImageConfirmOpen(false);
                toast.success('Görsel başarıyla kaldırıldı');
              } catch (error) {
                toast.error(error.message);
              }
            }}
            color="error"
            variant="contained"
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetail;